import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { ArrowUpRight, Leaf, Trees, Bird, Sprout, ShieldCheck, HeartHandshake } from 'lucide-react';
const SectionWrapper = styled.section`
    width: 100%;
    background: #f5f8f2;

    @media (max-width: 768px) {
        padding: 0 0 32px;
    }
`;

const SectionInner = styled.div`
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
`;

const LandscapeShowcase = styled.div`
    position: relative;
    width: 100%;
    min-height: 760px;
    border-top: 1px solid rgba(221, 237, 208, 0.7);
    border-bottom: 1px solid rgba(221, 237, 208, 0.7);
    background-color: #01553f;
    overflow: hidden;
    isolation: isolate;

    @media (max-width: 1280px) {
        min-height: 720px;
    }

    @media (max-width: 1100px) {
        min-height: 680px;
    }

    @media (max-width: 768px) {
        min-height: auto;
        display: flex;
        flex-direction: column;
    }
`;

const TextureLayer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 0;
    background-image: ${({ textureImage }) => (textureImage ? `url(${textureImage})` : 'none')};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    mix-blend-mode: multiply;
    opacity: 0.5;
    pointer-events: none;
`;

const GradientLayer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 0;
    background: linear-gradient(
            90deg,
            rgba(1, 58, 43, 0.56) 0%,
            rgba(1, 58, 43, 0.18) 28%,
            rgba(1, 58, 43, 0.06) 54%,
            rgba(1, 58, 43, 0) 75%
        ),
        linear-gradient(180deg, rgba(1, 84, 62, 0.2) 0%, rgba(1, 84, 62, 0) 100%);
    pointer-events: none;

    @media (max-width: 768px) {
        background: linear-gradient(
                180deg,
                rgba(1, 58, 43, 0.06) 0%,
                rgba(1, 58, 43, 0.18) 24%,
                rgba(1, 58, 43, 0.38) 100%
            ),
            linear-gradient(90deg, rgba(1, 58, 43, 0.12) 0%, rgba(1, 58, 43, 0) 65%);
    }
`;

const MapLayer = styled.div`
    position: absolute;
    left: -3%;
    top: -7%;
    bottom: -10%;
    width: 21%;
    z-index: 1;
    background-image: ${({ mapImage }) => (mapImage ? `url(${mapImage})` : 'none')};
    background-size: cover;
    background-position: left center;
    background-repeat: no-repeat;
    transform: rotate(-8deg);
    transform-origin: center center;
    pointer-events: none;

    @media (max-width: 1280px) {
        left: -9%;
        width: 40%;
        transform: rotate(-7deg);
    }

    @media (max-width: 1100px) {
        left: -11%;
        width: 42%;
        transform: rotate(-6deg);
    }

    @media (max-width: 768px) {
        left: -18%;
        top: auto;
        bottom: -10%;
        width: 92%;
        height: 56%;
        transform: rotate(-7deg);
        background-position: left bottom;
    }

    @media (max-width: 480px) {
        left: -22%;
        width: 100%;
        height: 50%;
        transform: rotate(-6deg);
    }
`;

const DesktopLayout = styled.div`
    position: relative;
    z-index: 2;
    padding: 56px 82px 56px 82px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 42px;

    @media (max-width: 1280px) {
        padding: 48px 56px 52px 56px;
        gap: 28px;
    }

    @media (max-width: 1100px) {
        padding: 40px 32px 44px;
        gap: 18px;
    }

    @media (max-width: 768px) {
        min-height: auto;
        padding: 28px 0 0;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 18px;
    }
`;

const CardsArea = styled.div`
    position: relative;
    width: 520px;
    min-width: 520px;
    height: 560px;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;

    @media (max-width: 1280px) {
        width: 470px;
        min-width: 470px;
        height: 530px;
    }

    @media (max-width: 1100px) {
        width: 430px;
        min-width: 430px;
        height: 500px;
    }

    @media (max-width: 768px) {
        order: 2;
        width: 100%;
        min-width: 100%;
        height: auto;
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 0 12px 16px;
    }

    @media (max-width: 480px) {
        padding: 0 10px 14px;
    }
`;

const TextArea = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 14px;
    max-width: 560px;
    margin-left: auto;
    padding-top: 0;
    padding-right: 44px;
    min-height: 560px;

    @media (max-width: 1280px) {
        max-width: 500px;
        padding-top: 0;
        padding-right: 12px;
        min-height: 530px;
    }

    @media (max-width: 1100px) {
        max-width: 430px;
        padding-top: 0;
        padding-right: 0;
        min-height: 500px;
    }

    @media (max-width: 768px) {
        order: 1;
        width: 100%;
        max-width: 100%;
        padding: 0 24px;
        margin-left: 0;
        gap: 10px;
        min-height: auto;
    }

    @media (max-width: 480px) {
        padding: 0 16px;
    }
`;

const SectionLabel = styled.p`
    margin: 0;
    color: #6ea44c;
    font-family: 'Montserrat', sans-serif;
    font-size: 19.2px;
    font-style: normal;
    font-weight: 400;
    line-height: 180%;
    letter-spacing: 3.84px;
    text-transform: uppercase;

    @media (max-width: 768px) {
        font-size: 12px;
        letter-spacing: 2.4px;
        line-height: 160%;
    }
`;

const SectionHeading = styled.h2`
    width: 100%;
    max-width: 520px;
    margin: 0;
    color: #f8fbf6;
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: 42px;

    @media (max-width: 1100px) {
        font-size: 28px;
        line-height: 38px;
    }

    @media (max-width: 768px) {
        max-width: 100%;
        font-size: 24px;
        line-height: 32px;
    }
`;

const SectionBody = styled.p`
    margin: 0;
    color: #fff;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 175%;
    max-width: 500px;

    @media (max-width: 1100px) {
        max-width: 430px;
    }

    @media (max-width: 768px) {
        font-size: 14px;
        line-height: 170%;
        max-width: 100%;
    }
`;
const cardFrontStyles = css`
    width: 358px;
    padding: 12px 16px;
    gap: 20px;
    border-radius: 12px;
    box-shadow: 3px 3px 20px 0 rgba(0, 0, 0, 0.07);

    @media (max-width: 1280px) {
        width: 340px;
    }

    @media (max-width: 1100px) {
        width: 320px;
    }

    @media (max-width: 768px) {
        width: 100%;
        padding: 12px 14px;
        gap: 18px;
        border-radius: 12px;
        box-shadow: 3px 3px 20px 0 rgba(0, 0, 0, 0.07);
    }
`;

const cardBackStyles = css`
    width: 257px;
    padding: 8.615px 11.486px;
    gap: 14.358px;
    border-radius: 8.615px;
    box-shadow: 2.154px 2.154px 14.358px 0 rgba(0, 0, 0, 0.07);

    @media (max-width: 768px) {
        width: 100%;
        padding: 12px 14px;
        gap: 18px;
        border-radius: 12px;
        box-shadow: 3px 3px 20px 0 rgba(0, 0, 0, 0.07);
    }
`;

const LandscapeCard = styled.div`
    position: absolute;
    top: ${({ isFront }) => (isFront ? '34px' : '94px')};
    left: ${({ isFront }) => (isFront ? '160px' : '56px')};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background: #fff;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: transform 0.25s ease, box-shadow 0.25s ease, left 0.25s ease, top 0.25s ease,
        width 0.25s ease;
    z-index: ${({ isFront }) => (isFront ? 3 : 2)};

    ${({ isFront }) => (isFront ? cardFrontStyles : cardBackStyles)}

    &:hover {
        transform: translateY(-2px);
    }

    @media (max-width: 1280px) {
        left: ${({ isFront }) => (isFront ? '132px' : '40px')};
        top: ${({ isFront }) => (isFront ? '28px' : '88px')};
    }

    @media (max-width: 1100px) {
        left: ${({ isFront }) => (isFront ? '104px' : '24px')};
        top: ${({ isFront }) => (isFront ? '24px' : '82px')};
    }

    @media (max-width: 768px) {
        position: relative;
        top: auto;
        left: auto;
        transform: none;
        z-index: 1;
        order: ${({ isFront }) => (isFront ? 1 : 2)};
    }
`;

const CardTop = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

const CardHeadingWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ compact }) => (compact ? '4px' : '6px')};
`;

const LandscapeName = styled.h3`
    margin: 0;
    color: #004d37;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '22px' : '28px')};
    font-style: normal;
    font-weight: 500;
    line-height: ${({ compact }) => (compact ? '30px' : '36px')};

    @media (max-width: 1100px) {
        font-size: ${({ compact }) => (compact ? '18px' : '24px')};
        line-height: ${({ compact }) => (compact ? '26px' : '32px')};
    }

    @media (max-width: 768px) {
        font-size: 18px;
        line-height: 26px;
    }
`;

const PartnerText = styled.p`
    margin: 0;
    color: #003626;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '12px' : '15px')};
    font-style: normal;
    font-weight: 400;
    line-height: 165%;

    @media (max-width: 1100px) {
        font-size: ${({ compact }) => (compact ? '11px' : '14px')};
    }

    @media (max-width: 768px) {
        font-size: 12px;
    }
`;

const ArrowBadge = styled.div`
    width: ${({ compact }) => (compact ? '22px' : '28px')};
    height: ${({ compact }) => (compact ? '22px' : '28px')};
    min-width: ${({ compact }) => (compact ? '22px' : '28px')};
    border-radius: 8px;
    border: 1px solid #dde5d8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #004d37;
    background: #fafcf9;
    margin-top: 2px;
    text-decoration: none;

    svg {
        width: ${({ compact }) => (compact ? '13px' : '16px')};
        height: ${({ compact }) => (compact ? '13px' : '16px')};
        stroke-width: 2.2;
    }

    @media (max-width: 768px) {
        width: 26px;
        height: 26px;
        min-width: 26px;
    }
`;

const CardImpactStats = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    row-gap: ${({ compact }) => (compact ? '6px' : '8px')};
    column-gap: 12px;
`;

const ImpactLabel = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: #424a46;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '11px' : '14px')};
    font-style: normal;
    font-weight: 400;
    line-height: 150%;

    svg {
        width: ${({ compact }) => (compact ? '12px' : '14px')};
        height: ${({ compact }) => (compact ? '12px' : '14px')};
        color: #7da95f;
        stroke-width: 2.1;
        flex-shrink: 0;
    }

    @media (max-width: 1100px) {
        font-size: ${({ compact }) => (compact ? '10px' : '13px')};
    }

    @media (max-width: 768px) {
        font-size: 13px;
    }

    @media (max-width: 420px) {
        font-size: 11px;
    }
`;

const ImpactValue = styled.div`
    color: #004d37;
    text-align: right;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '15px' : '18px')};
    font-style: normal;
    font-weight: 500;
    line-height: ${({ compact }) => (compact ? '22px' : '25px')};

    @media (max-width: 1100px) {
        font-size: ${({ compact }) => (compact ? '14px' : '17px')};
    }

    @media (max-width: 768px) {
        font-size: 18px;
        line-height: 24px;
    }

    @media (max-width: 420px) {
        font-size: 16px;
    }
`;

const StatsBoxGrid = styled.div`
    display: flex;
    width: 100%;
    align-items: flex-start;
    align-content: flex-start;
    gap: ${({ compact }) => (compact ? '9px' : '12px')};
    flex-wrap: wrap;
`;

const MiniStatBox = styled.div`
    display: flex;
    width: ${({ compact }) => (compact ? 'calc(50% - 4.5px)' : 'calc(50% - 6px)')};
    min-height: ${({ compact }) => (compact ? '64px' : '88px')};
    padding: ${({ compact }) => (compact ? '8px' : '12px')};
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    flex-shrink: 0;
    border-radius: ${({ compact }) => (compact ? '6px' : '8px')};
    background: #f8fbf6;

    @media (max-width: 1100px) {
        min-height: ${({ compact }) => (compact ? '60px' : '76px')};
        padding: ${({ compact }) => (compact ? '8px' : '10px')};
    }

    @media (max-width: 768px) {
        width: calc(50% - 6px);
        min-height: 72px;
        padding: 12px;
        border-radius: 8px;
    }

    @media (max-width: 420px) {
        width: calc(50% - 0);
        min-height: 66px;
        padding: 10px;
    }
`;

const MiniStatLabelRow = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
`;

const MiniStatLabel = styled.p`
    margin: 0;
    color: #424a46;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '10px' : '12px')};
    font-style: normal;
    font-weight: 400;
    line-height: 120%;

    @media (max-width: 1100px) {
        font-size: ${({ compact }) => (compact ? '9px' : '11px')};
    }

    @media (max-width: 768px) {
        font-size: 12px;
    }

    @media (max-width: 420px) {
        font-size: 10px;
    }
`;

const MiniIconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7da95f;
    flex-shrink: 0;

    svg {
        width: ${({ compact }) => (compact ? '11px' : '13px')};
        height: ${({ compact }) => (compact ? '11px' : '13px')};
        stroke-width: 2.1;

        @media (max-width: 768px) {
            width: 13px;
            height: 13px;
        }
    }
`;

const MiniStatValueRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const MiniStatValue = styled.p`
    margin: 0;
    color: #004d37;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '15px' : '18px')};
    font-style: normal;
    font-weight: 500;
    line-height: ${({ compact }) => (compact ? '21px' : '25px')};

    @media (max-width: 1100px) {
        font-size: ${({ compact }) => (compact ? '14px' : '16px')};
        line-height: ${({ compact }) => (compact ? '20px' : '23px')};
    }

    @media (max-width: 768px) {
        font-size: 18px;
        line-height: 24px;
    }

    @media (max-width: 420px) {
        font-size: 15px;
    }
`;

const DeltaTag = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${({ compact }) => (compact ? '2px 5px' : '2px 7px')};
    border-radius: 999px;
    background: #dcebc9;
    color: #6ea44c;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '9px' : '12px')};
    font-style: normal;
    font-weight: 500;
    line-height: 120%;
`;

const ThreatenedSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ compact }) => (compact ? '9px' : '12px')};
    align-self: stretch;
`;

const ThreatenedTitle = styled.p`
    margin: 0;
    color: #424a46;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '11px' : '14px')};
    font-style: normal;
    font-weight: 400;
    line-height: 150%;

    @media (max-width: 1100px) {
        font-size: ${({ compact }) => (compact ? '10px' : '13px')};
    }
`;

const SpeciesGrid = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${({ compact }) => (compact ? '6px' : '8px')};

    @media (max-width: 768px) {
        gap: 8px;
    }
`;

const SpeciesCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ compact }) => (compact ? '4px' : '6px')};
`;

const SpeciesImage = styled.div`
    width: 100%;
    aspect-ratio: 1.12 / 1;
    border-radius: ${({ compact }) => (compact ? '6px' : '8px')};
    background-image: ${({ image }) => (image ? `url(${image})` : 'none')};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-color: #d9e5d1;
    overflow: hidden;
`;

const SpeciesName = styled.p`
    margin: 0;
    color: #424a46;
    text-align: center;
    font-family: 'Montserrat', sans-serif;
    font-size: ${({ compact }) => (compact ? '9px' : '12px')};
    font-style: normal;
    font-weight: 500;
    line-height: 120%;

    @media (max-width: 1100px) {
        font-size: ${({ compact }) => (compact ? '8px' : '11px')};
    }

    @media (max-width: 768px) {
        font-size: 11px;
    }
`;

const getTopStats = landscape => [
    {
        icon: ShieldCheck,
        label: 'Hectares protected',
        value: landscape.hectaresProtected,
    },
    {
        icon: HeartHandshake,
        label: 'Guardians supported',
        value: landscape.guardiansSupported,
    },
];

const getMiniStats = landscape => [
    {
        icon: Trees,
        label: 'Landscape extent',
        value: landscape.landscapeExtent,
    },
    {
        icon: Leaf,
        label: 'People benefitted',
        value: landscape.peopleBenefitted,
    },
    {
        icon: Bird,
        label: 'Landscape partners',
        value: landscape.landscapePartners,
    },
    {
        icon: Sprout,
        label: 'Contributed (last year)',
        value: landscape.lastYearContribution,
    },
];

const LandscapeImpactSection = ({
    partnerName = 'Explorandes',
    numberOfCards = 2,
    landscapes = [],
    images,
}) => {
    const [activeCardIndex, setActiveCardIndex] = useState(0);

    const visibleCards = useMemo(() => {
        return landscapes.slice(0, numberOfCards);
    }, [landscapes, numberOfCards]);

    const activeLandscape = useMemo(() => {
        if (!visibleCards.length) return null;
        return visibleCards[activeCardIndex] || visibleCards[0];
    }, [visibleCards, activeCardIndex]);

    const orderedCards = useMemo(() => {
        if (visibleCards.length <= 1) return visibleCards;

        const active = visibleCards[activeCardIndex];
        const passive = visibleCards.find((_, index) => index !== activeCardIndex);

        return [
            passive && {
                ...passive,
                _isFront: false,
                _originalIndex: visibleCards.findIndex(item => item.id === passive.id),
            },
            active && { ...active, _isFront: true, _originalIndex: activeCardIndex },
        ].filter(Boolean);
    }, [visibleCards, activeCardIndex]);

    const headingText = activeLandscape
        ? `${partnerName} is supporting ${activeLandscape.landscapeName} to prevent deforestation and restore native ecosystems.`
        : `${partnerName} is preventing deforestation and restoring native ecosystems.`;

    const descriptionText =
        (activeLandscape && activeLandscape.description) ||
        'Their contributions allow indigenous and local Guardians to protect and restore land.';
    const getLandscapeSlug = name => {
        if (!name) return '';
        return name
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '-');
    };

    const getLandscapeUrl = landscapeName => {
        const slug = getLandscapeSlug(landscapeName);
        return `https://regenera.earth/landscapes/${slug}`;
    };
    return (
        <SectionWrapper>
            <SectionInner>
                <LandscapeShowcase>
                    <TextureLayer textureImage={images && images.roughTexture} />
                    <GradientLayer />
                    <MapLayer mapImage={images && images.mapBackground} />

                    <DesktopLayout>
                        <CardsArea>
                            {orderedCards.map(card => {
                                const compact = !card._isFront;
                                const topStats = getTopStats(card);
                                const miniStats = getMiniStats(card);

                                return (
                                    <LandscapeCard
                                        key={card.id}
                                        isFront={card._isFront}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setActiveCardIndex(card._originalIndex)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setActiveCardIndex(card._originalIndex);
                                            }
                                        }}
                                        aria-label={`Open ${card.landscapeName} card`}
                                    >
                                        <CardTop>
                                            <CardHeadingWrap compact={compact}>
                                                <LandscapeName compact={compact}>
                                                    {card.landscapeName}
                                                </LandscapeName>
                                                <PartnerText compact={compact}>
                                                    Impact of {partnerName}
                                                </PartnerText>
                                            </CardHeadingWrap>
                                            <ArrowBadge
                                                compact={compact}
                                                as="a"
                                                href={getLandscapeUrl(card.landscapeName)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={e => e.stopPropagation()}
                                                aria-label={`Open ${card.landscapeName} landscape page`}
                                            >
                                                <ArrowUpRight />
                                            </ArrowBadge>
                                        </CardTop>

                                        <CardImpactStats compact={compact}>
                                            {topStats.map(stat => {
                                                const Icon = stat.icon;
                                                return (
                                                    <React.Fragment key={stat.label}>
                                                        <ImpactLabel compact={compact}>
                                                            <Icon />
                                                            <span>{stat.label}</span>
                                                        </ImpactLabel>
                                                        <ImpactValue compact={compact}>
                                                            {stat.value}
                                                        </ImpactValue>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </CardImpactStats>

                                        <StatsBoxGrid compact={compact}>
                                            {miniStats.map(stat => {
                                                const Icon = stat.icon;
                                                return (
                                                    <MiniStatBox key={stat.label} compact={compact}>
                                                        <MiniStatLabelRow>
                                                            <MiniStatLabel compact={compact}>
                                                                {stat.label}
                                                            </MiniStatLabel>
                                                            <MiniIconWrap compact={compact}>
                                                                <Icon />
                                                            </MiniIconWrap>
                                                        </MiniStatLabelRow>

                                                        <MiniStatValueRow>
                                                            <MiniStatValue compact={compact}>
                                                                {stat.value}
                                                            </MiniStatValue>

                                                            {stat.delta ? (
                                                                <DeltaTag compact={compact}>
                                                                    {stat.delta}
                                                                </DeltaTag>
                                                            ) : null}
                                                        </MiniStatValueRow>
                                                    </MiniStatBox>
                                                );
                                            })}
                                        </StatsBoxGrid>

                                        <ThreatenedSection compact={compact}>
                                            <ThreatenedTitle compact={compact}>
                                                Threatened species protected:
                                            </ThreatenedTitle>

                                            <SpeciesGrid compact={compact}>
                                                {(card.species || []).slice(0, 3).map(species => (
                                                    <SpeciesCard key={species.id} compact={compact}>
                                                        <SpeciesImage
                                                            image={species.image}
                                                            compact={compact}
                                                        />
                                                        <SpeciesName compact={compact}>
                                                            {species.name}
                                                        </SpeciesName>
                                                    </SpeciesCard>
                                                ))}
                                            </SpeciesGrid>
                                        </ThreatenedSection>
                                    </LandscapeCard>
                                );
                            })}
                        </CardsArea>

                        <TextArea>
                            <SectionLabel>LOCATION</SectionLabel>
                            <SectionHeading>{headingText}</SectionHeading>
                            <SectionBody>{descriptionText}</SectionBody>
                        </TextArea>
                    </DesktopLayout>
                </LandscapeShowcase>
            </SectionInner>
        </SectionWrapper>
    );
};

export default LandscapeImpactSection;
